import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://allchinabuyspreadsheet.fit';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-32 pb-16 min-h-screen bg-background-50">
          <div className="px-4 md:px-6 max-w-4xl mx-auto text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <article>
          <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-background-100">
            <div className="px-4 md:px-6 max-w-4xl mx-auto text-center">
              <nav className="flex items-center justify-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">Home</Link>
                <i className="ri-arrow-right-s-line text-[10px]"></i>
                <span className="text-foreground-700 font-medium">{article.title}</span>
              </nav>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed mb-6">
                {article.subtitle}
              </p>
              <div className="flex items-center justify-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  Updated June 2026
                </span>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary-500 text-white font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  Open AllChinaBuy Sheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </section>

          <section className="py-8 md:py-12 bg-background-100">
            <div className="px-4 md:px-6 max-w-4xl mx-auto">
              <div className="rounded-2xl overflow-hidden h-56 md:h-80">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                  loading="eager"
                />
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 bg-background-50">
            <div className="px-4 md:px-6 max-w-4xl mx-auto">
              <div className="space-y-12">
                {article.sections.map((section, sIdx) => (
                  <div key={sIdx}>
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                      {section.heading}
                    </h2>
                    {section.content.map((paragraph, pIdx) => (
                      <p key={pIdx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                ))}
              </div>

              <div className="mt-14 bg-background-100 rounded-2xl p-8 md:p-10 border border-background-200/60 text-center">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                  Ready to Explore the AllChinaBuy Spreadsheet?
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6 max-w-lg mx-auto">
                  Browse verified products, trusted sellers, and organized categories — your next discovery is one click away.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="w-full sm:w-auto px-7 py-3.5 bg-primary-500 text-white font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    AllChinaBuy Sheet
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                  <Link
                    to="/"
                    className="w-full sm:w-auto px-7 py-3.5 bg-background-200 text-foreground-800 font-semibold text-sm rounded-full hover:bg-background-300 transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    Back to Home
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-background-200/60">
                <h3 className="text-sm font-semibold text-foreground-500 uppercase tracking-wider mb-4">More Articles</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {articles
                    .filter((a) => a.slug !== article.slug)
                    .slice(0, 6)
                    .map((a) => (
                      <Link
                        key={a.slug}
                        to={`/article/${a.slug}`}
                        className="flex items-center gap-2 p-3 bg-background-100 rounded-xl border border-background-200/60 hover:border-primary-200/50 transition-all duration-200 cursor-pointer group"
                      >
                        <span className="text-xs text-foreground-700 group-hover:text-primary-600 transition-colors leading-tight flex-1">
                          {a.title.length > 55 ? a.title.slice(0, 55) + '...' : a.title}
                        </span>
                        <i className="ri-arrow-right-line text-xs text-foreground-400 group-hover:text-primary-500 flex-shrink-0"></i>
                      </Link>
                    ))}
                </div>
              </div>
            </div>
          </section>
        </article>
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.description,
            image: article.imageUrl,
            datePublished: '2026-06-25',
            dateModified: '2026-06-25',
            keywords: [article.mainKeyword, ...article.secondaryKeywords].join(', '),
            author: {
              '@type': 'Organization',
              name: 'AllChinaBuy Spreadsheet',
              url: `${SITE_URL}/`,
            },
            publisher: {
              '@type': 'Organization',
              name: 'AllChinaBuy Spreadsheet',
              url: `${SITE_URL}/`,
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `${SITE_URL}/article/${article.slug}`,
            },
          }),
        }}
      />

      <Footer />
    </>
  );
}